import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, computed, ElementRef, HostListener, inject, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate, layoutTemplatesAreas } from '@src/assets/css/fullscreen.template.scss';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { SYSTEM_VS_GUID } from '@src/suite/common/enums/constants';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import {
    FullscreenActions,
    OpenFullscreenModalActions,
    selectCurrentRecord,
    selectCurrentSearchItemHeights,
    selectCurrentStoryIndex,
    selectCurrentStoryRelatives,
    selectFullscreenCurrentStory,
    selectFullscreenPinnedItems,
    selectFullscreenRecordType,
    selectFullscreenSearchItems,
    selectIsCurrentStoryStandalone,
    selectLayoutTemplate,
    selectLayoutTemplateClass,
    selectPinnedItemGuid,
    selectSearchTreeControl,
    selectSearchTreeDataSource,
    selectSidecarVisible,
} from '@src/suite/common/store/fullscreen';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectCurrentNodeUuid } from '@src/suite/common/store/list/list.selectors';
import { selectCurrentNodeIndex, selectTotalGroupedItem, selectTotalGroupedItemCount } from '@src/suite/common/store/playbook/playbook.selector';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { LiveActions } from '@src/suite/live/store/actions/live.actions';

@Component({
    selector: 'st-full-screen-dialog',
    templateUrl: './st-full-screen-dialog.html',
    styleUrls: ['./st-full-screen-dialog.scss'],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    // host: {
    //     class: BASE_TEMPLATE_CLASS,
    // },
})
export class StFullScreenDialogComponent {
    public store = inject(Store);
    ObjectTypesEnum = ObjectTypes;
    PSTFieldNames = PSTFieldNames;
    FullscreenSidecar = FullscreenLayoutAreas;
    NavigationDirection = NavigationDirection;

    virtualScrollViewPort = viewChild(CdkVirtualScrollViewport);
    searchBox = viewChild<ElementRef>('searchBox');
    PSTWebTotalResultsLabel: i18nKeys = i18nKeys.PSTWebTotalResultsLabel;
    templateItems: FullscreenLayoutTemplate[] = Object.keys(layoutTemplatesAreas) as FullscreenLayoutTemplate[];

    record = this.store.selectSignal(selectCurrentRecord);
    story = this.store.selectSignal(selectFullscreenCurrentStory);
    searchItems = this.store.selectSignal(selectFullscreenSearchItems);
    topbarItems = this.store.selectSignal(selectFullscreenPinnedItems);
    languageSettings = this.store.selectSignal(selectI18N);
    selectedPinItemGuid = this.store.selectSignal(selectPinnedItemGuid);
    selectedTemplate = this.store.selectSignal(selectLayoutTemplate);
    currentViewRecordIdx = this.store.selectSignal(selectCurrentNodeIndex);
    totalViewStateRecordsCount = this.store.selectSignal(selectTotalGroupedItemCount);
    totalGroupedItems = this.store.selectSignal(selectTotalGroupedItem);
    isCurrentStoryStandalone = this.store.selectSignal(selectIsCurrentStoryStandalone);
    currentStoryIndex = this.store.selectSignal(selectCurrentStoryIndex);
    fullScreenRecordType = this.store.selectSignal(selectFullscreenRecordType);
    currentStoryRelatives = this.store.selectSignal(selectCurrentStoryRelatives);
    currentNodeUuid = this.store.selectSignal(selectCurrentNodeUuid);

    sidecartMainVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_MAIN));
    sidecartSecondVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_SECOND));
    sidecartThirdVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_THIRD));
    sidecartFourthVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_FOURTH));
    templateLayoutClass$ = this.store.select(selectLayoutTemplateClass);
    cardItemHeights$ = this.store.select(selectCurrentSearchItemHeights);
    treeDataSource$ = this.store.select(selectSearchTreeDataSource);
    selectTreeControl$ = this.store.select(selectSearchTreeControl);

    pinItemsSearchTerm = signal('');
    hoveredItemGuid = signal('');
    searchInput = signal('');
    pinnedItemsSerachTerm = signal('');

    showStoriesNavigation = computed(() => {
        return this.fullScreenRecordType() === ObjectTypes.SCRIPT && !this.isCurrentStoryStandalone();
    });

    currentIdx = computed(() => {
        if (this.showStoriesNavigation()) {
            return this.currentStoryIndex() + 1;
        } else {
            const idx = this.totalGroupedItems()?.findIndex(node => node.uniqueId === this.currentNodeUuid()) ?? -1;
            return idx === -1 ? 0 : idx + 1;
        }
    });

    currentMenuItem = computed(() => {
        const idx = this.currentIdx() - 1;

        if (idx > -1) {
            const items = this.matMenuSearchOptions();
            return items[idx];
        }
    });

    totalCount = computed(() => {
        if (this.showStoriesNavigation()) {
            return this.currentStoryRelatives().length;
        } else {
            return this.totalViewStateRecordsCount();
        }
    });

    recordType = computed(() => {
        switch (this.record()?.Type) {
            case ObjectTypes.TOPIC:
                return 'Topic';
            case ObjectTypes.ASSIGNMENT:
                return 'Assignment';
            case ObjectTypes.SCRIPT:
                return 'Story';
            case ObjectTypes.RUNDOWN:
                return 'Rundown';
            default:
                break;
        }
        return '';
    });

    matMenuSearchOptions = computed(() => {
        const trimmedSearchInput = this.searchInput().trim();
        const getTooltip = (rec: StorytellingRecord) => {
            if (rec?.ContainerTitle) {
                const containerData = `${rec?.Title} [${rec?.ContainerTitle} ${rec?.ContainerModTime.date.toLocaleDateString()}]`;
                return `${rec?.FullStorySlug || containerData} `;
            } else {
                return rec?.Title;
            }
        };
        if (this.showStoriesNavigation()) {
            const currentStoryRelatives = this.currentStoryRelatives().map(story => ({
                uniqueId: story?.Guid,
                object: story,
                containerModTimeDate: story?.ContainerModTime.date.toLocaleDateString(),
                tooltip: getTooltip(story),
            }));
            if (!this.searchInput()) {
                return currentStoryRelatives;
            } else {
                return currentStoryRelatives.filter(story => story?.object?.Title?.toLowerCase().includes(trimmedSearchInput.toLowerCase()));
            }
        } else {
            if (!this.searchInput()) {
                return this.totalGroupedItems()?.map(node => ({
                    uniqueId: node.uniqueId,
                    object: node.object,
                    containerModTimeDate: node.object?.ModTime.date.toLocaleDateString(),
                    tooltip: getTooltip(node.object),
                }));
            } else
                return this.totalGroupedItems()
                    ?.map(node => ({
                        uniqueId: node.uniqueId,
                        object: node.object,
                        containerModTimeDate: node.object?.ModTime.date.toLocaleDateString(),
                        tooltip: getTooltip(node.object),
                    }))
                    ?.filter(record => record?.object?.Title?.toLowerCase().includes(trimmedSearchInput.toLowerCase()));
        }
    });

    selectedTemplateIndex = computed(() => {
        return this.templateItems.indexOf(this.selectedTemplate());
    });

    filteredPinnedItems = computed(() => {
        const searchTerm = this.pinnedItemsSerachTerm();
        return this.topbarItems().filter(item => item?.Title?.toLowerCase().includes(searchTerm.toLowerCase()));
    });

    noBorderIndexes = computed(() => {
        if (!this.selectedPinItemGuid()) {
            return [];
        }
        const selectedItemIdx = this.topbarItems().findIndex(item => item?.Guid === this.selectedPinItemGuid());
        if (selectedItemIdx === -1) {
            return [];
        }
        if (selectedItemIdx === this.topbarItems().length - 1) {
            return [selectedItemIdx];
        }
        return [selectedItemIdx, selectedItemIdx + 1];
    });

    isCreate = computed(() => {
        return this.record()?.isNewRecord;
    });

    topicData = computed(() => {
        return this.store.selectSignal<StorytellingTopicRecord>(selectCurrentRecord)();
    });

    @HostListener('document:keyup.arrowleft', ['$event'])
    public navigateBack(event) {
        this.navigate(event, NavigationDirection.PREVIOUS);
    }
    @HostListener('document:keyup.arrowright', ['$event'])
    public navigateForward(event) {
        this.navigate(event, NavigationDirection.NEXT);
    }

    onSearchInputChange(event: Event) {
        this.searchInput.set((event.target as HTMLInputElement).value);
    }

    clearSearch(event: Event) {
        event.stopPropagation();
        this.searchInput.set('');
        this.onMenuOpened();
    }

    onMenuOpened() {
        const input = this.searchBox().nativeElement as HTMLInputElement;
        input.focus();
    }

    onPinnedItemChanged(itemGuid: string, type: ObjectTypes) {
        if (itemGuid && type) {
            switch (type) {
                case ObjectTypes.TOPIC:
                    this.store.dispatch(FullscreenActions.openTopic({ recordGuid: itemGuid, recordType: type, fromPinnedItem: true }));
                    break;
                case ObjectTypes.SCRIPT:
                    this.store.dispatch(FullscreenActions.openStory({ recordGuid: itemGuid, recordType: type, fromPinnedItem: true }));
                    break;
                case ObjectTypes.RUNDOWN:
                    this.store.dispatch(FullscreenActions.openRundown({ recordGuid: itemGuid, recordType: type, fromPinnedItem: true }));
            }
        }
    }

    onTemplateChanged(event: MatButtonToggleChange) {
        this.store.dispatch(FullscreenActions.setFullscreenTemplate({ template: event.value }));
    }

    searchItemExecute(searchTerm: string) {
        this.pinItemsSearchTerm.set(searchTerm);
        this.store.dispatch(FullscreenActions.setSearchTerm({ searchTerm }));
    }

    searchPinnedItemExecute(searchTerm: string) {
        this.pinnedItemsSerachTerm.set(searchTerm);
    }

    closeModal() {
        this.store.dispatch(OpenFullscreenModalActions.closeFullscreenModal());
    }

    selectItem(guid: string, type: ObjectTypes) {
        switch (type) {
            case ObjectTypes.TOPIC:
                this.store.dispatch(FullscreenActions.openTopic({ recordGuid: guid, recordType: type, fromPinnedItem: true }));
                break;
            case ObjectTypes.SCRIPT:
                this.store.dispatch(FullscreenActions.openStory({ recordGuid: guid, recordType: type, fromPinnedItem: true }));
                break;
        }
    }

    hoverItem(guid: string) {
        this.hoveredItemGuid.set(guid);
    }

    pinItem(itemGuid: string) {
        this.store.dispatch(FullscreenActions.pinItem({ itemGuid }));
    }

    unpinItem(itemGuid: string) {
        this.store.dispatch(FullscreenActions.unpinItem({ itemGuid }));
    }

    openHomeItem() {
        this.store.dispatch(FullscreenActions.openHomeItem());
    }

    trackByGuid(index, node) {
        if (node) {
            return node?.object?.Guid || node?.id || node;
        }
        return node;
    }

    toggleNode(treeControl, node) {
        treeControl.toggle(node);
    }

    onPinItemMenuOpened() {
        this.virtualScrollViewPort().checkViewportSize();
    }

    public navigate(event, direction: NavigationDirection) {
        if (
            event &&
            event.target &&
            event.target.tagName &&
            !(event.target.tagName.toLowerCase() === 'input' || event.target.tagName.toLowerCase() === 'textarea' || event.target.classList.contains('fr-element'))
        ) {
            this.store.dispatch(FullscreenActions.navigateRecord({ direction, throughTopicStories: this.showStoriesNavigation() }));
        }
    }

    /**
     * Navigates to a different record directly. If the current navigation is on stories,
     * then it will navigate to the next story. Otherwise, it will navigate to the next record
     * of the given type.
     * @param nextRecordGuid The GUID of the record to navigate to
     */
    navigateDirectly(nextRecordGuid: string) {
        this.searchInput.set('');
        this.store.dispatch(
            FullscreenActions.navigateRecordDirectly({
                uniqueNodeId: nextRecordGuid,
                throughTopicStories: this.showStoriesNavigation(),
            })
        );
    }

    openRoStory(guid: string) {
        this.store.dispatch(LiveActions.openRundownStoryFullScreen({ guid }));
    }

    onAssignmentOpenHandler(record: StorytellingAssignmentRecord) {
        this.store.dispatch(FullscreenActions.openAssignment({ recordGuid: record.Guid, recordType: ObjectTypes.ASSIGNMENT, sidecarMenu: FullscreenLayoutAreas.SIDECAR_MAIN, vsGuid: SYSTEM_VS_GUID }));
    }

    onStoryOpenHandler(story: StorytellingStoryRecord) {
        this.store.dispatch(FullscreenActions.openStory({ recordGuid: story.Guid, recordType: story.Type }));
    }
}
